import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ScanLine, Loader2, X, Check, Lock, ChevronRight, Scale, Search } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { DiscoveredTool, RegistryRow } from '../../types/aai';
import { useParticipant } from '../../hooks/useParticipant';
import { Loading, ErrorNote } from './components';
import { RiskPill, PageHeader } from './dashboards/ui';

const CAPABILITIES = ['summarization', 'drafting', 'analysis', 'automation', 'other'];
const EU_CLASSES = ['prohibited', 'high', 'limited', 'minimal'];
const NIST_FUNCTIONS = ['govern', 'map', 'measure', 'manage'];

const EU_CLS: Record<string, string> = {
  prohibited: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700',
  limited: 'bg-amber-50 text-amber-700', minimal: 'bg-emerald-50 text-emerald-700',
};
const STATUS_CLS: Record<string, string> = {
  Live: 'bg-emerald-50 text-emerald-700', 'Shadow AI': 'bg-amber-100 text-amber-700', Blocked: 'bg-red-100 text-red-700',
};

const Pill: React.FC<{ text?: string | null; cls?: string; mono?: boolean }> = ({ text, cls, mono }) =>
  text && text !== '—' ? (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${mono ? 'font-sans uppercase tracking-wide' : ''} ${cls || 'bg-gray-100 text-gray-600'}`}>{text}</span>
  ) : <span className="text-gray-300">—</span>;

// ── the five lenses (design §5): one inventory, read per audience ─────────

type LensKey = 'executive' | 'financial' | 'technical' | 'operational' | 'examiner';

const LENSES: Record<LensKey, { label: string; desc: string; cols: { key: string; label: string }[] }> = {
  executive: {
    label: 'Executive',
    desc: 'The portfolio at a glance — risk and value, no detail noise.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'dept', label: 'Dept' }, { key: 'risk', label: 'Risk' },
      { key: 'value', label: 'Est. value / yr' }, { key: 'status', label: 'Status' },
    ],
  },
  financial: {
    label: 'Financial',
    desc: 'Cost vs value as ranges (never fake precision) plus shelfware exposure.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'tool', label: 'Tool / vendor' }, { key: 'cost', label: 'Est. cost / yr' },
      { key: 'value', label: 'Est. value / yr' }, { key: 'overlap', label: 'Overlap' },
    ],
  },
  technical: {
    label: 'Technical',
    desc: 'What model, where data goes, and whether a human checks the output.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'model', label: 'Model' }, { key: 'flow', label: 'Data flow' },
      { key: 'human', label: 'Human review' }, { key: 'tool', label: 'Tool / vendor' },
    ],
  },
  operational: {
    label: 'Operational',
    desc: 'The working view — ownership, capability, and where each case stands.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'dept', label: 'Dept' }, { key: 'owner', label: 'Owner' },
      { key: 'capability', label: 'Capability' }, { key: 'status', label: 'Status' },
    ],
  },
  examiner: {
    label: 'Examiner',
    desc: 'The compliance artifact — framework mapping and approval provenance.',
    cols: [
      { key: 'usecase', label: 'Use case' }, { key: 'eu', label: 'EU AI Act' }, { key: 'nist', label: 'NIST' },
      { key: 'iso', label: 'ISO 42001' }, { key: 'risk', label: 'Risk' }, { key: 'approver', label: 'Approved by' },
    ],
  },
};
const LENS_ORDER: LensKey[] = ['executive', 'financial', 'technical', 'operational', 'examiner'];

// Which lens a role lands on, and whether it may switch. Dept heads live in the
// operational day-to-day (server pins them to their department); experts review
// through the technical lens only; owners start at the portfolio view.
function lensPolicy(role: string | null): { initial: LensKey; locked: boolean } {
  if (role === 'EXPERT') return { initial: 'technical', locked: true };
  if (role === 'DEPARTMENT_HEAD') return { initial: 'operational', locked: true };
  if (role === 'OWNER') return { initial: 'executive', locked: false };
  return { initial: 'operational', locked: false };
}

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null);

const cellFor = (key: string, r: RegistryRow): React.ReactNode => {
  switch (key) {
    case 'usecase': return <span className="font-semibold text-gray-900">{r.name}</span>;
    case 'dept': return <span className="text-gray-600">{r.department}</span>;
    case 'risk': return <RiskPill risk={r.risk} />;
    case 'value': return r.est_value_band !== '—' ? <span className="text-gray-800">{r.est_value_band}</span> : <span className="text-gray-300">—</span>;
    case 'cost': return r.est_cost_band !== '—' ? <span className="text-gray-800">{r.est_cost_band}</span> : <span className="text-gray-300">—</span>;
    case 'status': return <Pill text={r.status} cls={STATUS_CLS[r.status]} />;
    case 'tool': return <span className="text-gray-600">{r.tool_vendor === '—' ? <span className="text-gray-300">—</span> : r.tool_vendor}</span>;
    case 'overlap': return r.overlap ? <Pill text="Overlap — check shelfware" cls="bg-amber-50 text-amber-700" /> : <span className="text-gray-300">—</span>;
    case 'model': return <span className="text-gray-600">{r.model === '—' ? <span className="text-gray-300">—</span> : r.model}</span>;
    case 'flow': return <span className="text-gray-600 whitespace-normal">{r.data_flow === '—' ? <span className="text-gray-300">—</span> : r.data_flow}</span>;
    case 'human': return <span className="text-gray-600">{r.human_in_loop === 'Not set' ? <span className="text-gray-300">— unset</span> : r.human_in_loop}</span>;
    case 'owner': return <span className="text-gray-600">{r.owner === '—' ? <span className="text-gray-300">—</span> : r.owner}</span>;
    case 'capability': return <Pill text={r.capability_tag} mono cls="bg-brand-50 text-brand-700" />;
    case 'eu': return <Pill text={r.eu_ai_act_class} cls={EU_CLS[r.eu_ai_act_class || '']} />;
    case 'nist': return <Pill text={r.nist_function} mono cls="bg-purple-50 text-purple-700" />;
    case 'iso': return r.iso_control ? <span className="font-sans text-[11.5px] text-gray-600">{r.iso_control}</span> : <span className="text-gray-300">—</span>;
    case 'approver': return r.approved_by
      ? <span className="text-gray-600">{r.approved_by}{r.approved_at ? ` · ${fmtDate(r.approved_at)}` : ''}</span>
      : <span className="text-gray-400 italic">{r.status === 'Shadow AI' ? 'not approved' : r.status === 'Blocked' ? 'never approved' : '—'}</span>;
    default: return <span className="text-gray-300">—</span>;
  }
};

const DISMISS_KEY = 'aai_registry_discovery_dismissed';
const readDismissed = (): string[] => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); } catch { return []; } };

const RegistryPage: React.FC = () => {
  const { context, ready } = useParticipant();
  const role = context?.workspace_role ?? null;
  const canCompany = !!context?.capabilities.view_company_dashboard; // scope + shadow-log + discovery
  const canManage = !!context?.capabilities.manage_program;          // start evaluations

  const policy = lensPolicy(role);
  const [lens, setLens] = useState<LensKey | null>(null);
  const activeLens: LensKey = lens ?? policy.initial;

  const [rows, setRows] = useState<RegistryRow[]>([]);
  const [scopedDept, setScopedDept] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // shadow-AI capture + discovery banner
  const [shadowOpen, setShadowOpen] = useState(false);
  const [discovered, setDiscovered] = useState<DiscoveredTool[]>([]);
  const [dismissed, setDismissed] = useState<string[]>(readDismissed);
  const [logging, setLogging] = useState(false);
  // record drawer + scope modal
  const [selected, setSelected] = useState<RegistryRow | null>(null);
  const [scoping, setScoping] = useState<RegistryRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await aaiApi.getRegistry();
      setRows(data.entries);
      setScopedDept(data.scoped_department ?? null);
      setError(null);
    } catch { setError('Could not load the registry.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!ready || !canCompany) return;
    aaiApi.getDiscoveredTools().then(({ data }) => setDiscovered(data.discovered)).catch(() => undefined);
  }, [ready, canCompany]);

  const banner = useMemo(() => discovered.filter((d) => !dismissed.includes(d.tool)), [discovered, dismissed]);

  const dismissDiscovery = () => {
    const next = Array.from(new Set([...dismissed, ...banner.map((b) => b.tool)]));
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    setDismissed(next);
  };
  const logDiscovery = async () => {
    setLogging(true);
    try {
      await aaiApi.logDiscoveredTools(banner.map((b) => b.tool));
      setDiscovered([]);
      await load();
    } finally { setLogging(false); }
  };

  if (!ready) return <Loading />;

  const cfg = LENSES[activeLens];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="Every AI use case, one record."
          subtitle="One inventory, read through the lens that fits you. Approved requests and captured shadow AI, each mapped to risk, framework, owner, cost, and the tool powering it."
        />
        {canCompany && (
          <button onClick={() => setShadowOpen(true)}
            className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-2 whitespace-nowrap"><ScanLine size={14} /> Log shadow AI</button>
        )}
      </div>

      {/* lens switcher */}
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <span className="font-sans text-[10px] uppercase tracking-widest font-semibold text-gray-400">Lens</span>
        <div className="flex gap-1.5 flex-wrap">
          {LENS_ORDER.map((k) => {
            const on = k === activeLens;
            return (
              <button key={k} onClick={() => { if (!policy.locked) setLens(k); }} disabled={policy.locked && !on}
                className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold border transition-colors ${on ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white border-gray-200 text-gray-500'} ${policy.locked && !on ? 'opacity-50 cursor-not-allowed' : on ? '' : 'hover:border-brand-300'}`}>
                {LENSES[k].label}
              </button>
            );
          })}
        </div>
        {policy.locked && (
          <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-400"><Lock size={12} /> Locked to {cfg.label} for this role</span>
        )}
      </div>
      <p className="text-[12.5px] text-gray-500 mb-4">{cfg.desc}</p>

      {/* shadow discovery banner */}
      {banner.length > 0 && (
        <div className="flex items-center gap-3.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3.5 mb-4">
          <span className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center"><Search size={17} /></span>
          <div className="flex-1 text-[13px] leading-relaxed">
            <b>Orbit discovered unregistered AI.</b>{' '}
            <span className="text-gray-600">
              {banner.slice(0, 3).map((b, i) => (
                <React.Fragment key={b.tool}>{i > 0 && (i === Math.min(banner.length, 3) - 1 ? ' and ' : ', ')}{b.count} {b.count === 1 ? 'person mentions' : 'people mention'} <b>{b.tool}</b></React.Fragment>
              ))}
              {banner.length > 3 ? ` (+${banner.length - 3} more)` : ''} during onboarding — not in your registry. Counts only, never names.
            </span>
          </div>
          <button onClick={logDiscovery} disabled={logging}
            className="shrink-0 inline-flex items-center gap-1.5 bg-black text-white rounded-lg px-3 py-2 text-[12px] font-semibold disabled:opacity-50 whitespace-nowrap">
            {logging && <Loader2 size={13} className="animate-spin" />} Log to registry
          </button>
          <button onClick={dismissDiscovery} className="shrink-0 text-[12px] text-gray-400 hover:text-gray-600">Dismiss</button>
        </div>
      )}

      {loading ? <Loading /> : error ? <ErrorNote message={error} /> : (
        <>
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px] whitespace-nowrap">
                <thead><tr className="text-left bg-gray-50/70 text-gray-400">
                  {cfg.cols.map((c) => (
                    <th key={c.key} className="px-3.5 py-2.5 font-sans text-[10px] uppercase tracking-wider font-semibold">{c.label}</th>
                  ))}
                  <th className="w-6" />
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id || r.name} onClick={() => setSelected(r)}
                      className={`border-t border-gray-50 cursor-pointer hover:bg-gray-50/60 ${r.status === 'Shadow AI' ? 'bg-amber-50/40' : ''} ${r.status === 'Blocked' ? 'bg-red-50/40' : ''}`}>
                      {cfg.cols.map((c) => (
                        <td key={c.key} className={`px-3.5 py-3 align-middle ${c.key === 'usecase' ? 'max-w-[240px] truncate' : c.key === 'flow' ? 'max-w-[260px] truncate' : ''}`} title={c.key === 'usecase' ? r.name : undefined}>
                          {cellFor(c.key, r)}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-gray-300"><ChevronRight size={14} /></td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={cfg.cols.length + 1} className="px-4 py-6 text-center text-[13px] text-gray-400">
                      No use cases yet — approved requests and logged shadow AI appear here.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="text-[12px] text-gray-400 mt-2.5">
            {rows.length} use case(s) · lens: {cfg.label}{scopedDept ? ` · scoped to ${scopedDept}` : ''}
          </div>
        </>
      )}

      {selected && (
        <RecordDrawer row={selected} canScope={canCompany} canManage={canManage}
          onClose={() => setSelected(null)} onScope={() => { setScoping(selected); setSelected(null); }} />
      )}
      {shadowOpen && <ShadowModal onClose={() => setShadowOpen(false)} onSaved={() => { setShadowOpen(false); load(); }} />}
      {scoping && <ScopeModal row={scoping} onClose={() => setScoping(null)} onSaved={() => { setScoping(null); load(); }} />}
    </div>
  );
};

// ── use-case record drawer (row click) ─────────────────────────────────────

const DrawerField: React.FC<{ label: string; wide?: boolean; children: React.ReactNode }> = ({ label, wide, children }) => (
  <div className={wide ? 'col-span-2' : ''}>
    <div className="font-sans text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-1">{label}</div>
    <div className="text-[13px] text-gray-800 leading-snug">{children}</div>
  </div>
);

const dash = (v?: string | null, unset = '—') =>
  !v || v === '—' || v === 'Not set' ? <span className="text-gray-300">{unset}</span> : <>{v}</>;

const RecordDrawer: React.FC<{
  row: RegistryRow; canScope: boolean; canManage: boolean; onClose: () => void; onScope: () => void;
}> = ({ row, canScope, canManage, onClose, onScope }) => {
  const navigate = useNavigate();
  const hasTool = row.tool_vendor && row.tool_vendor !== '—';
  const trail = buildTrail(row);
  const costValue = `${row.est_cost_band === '—' ? '—' : `${row.est_cost_band} / yr`}  →  ${row.est_value_band === '—' ? '—' : `${row.est_value_band} / yr`}`;

  const evaluate = () => {
    if (row.evaluation_id) navigate(`/aai/vendors?open=${encodeURIComponent(row.evaluation_id)}`);
    else navigate(`/aai/vendors?evaluate=${encodeURIComponent(row.tool_vendor)}`);
  };
  const showEvalCta = hasTool && (row.evaluation_id || canManage);

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/30" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 z-[61] w-[440px] max-w-[92vw] bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-5 border-b border-gray-100 flex items-start gap-3">
          <div className="flex-1">
            <div className="font-sans text-[10px] uppercase tracking-widest font-semibold text-gray-400 mb-1">Use case record</div>
            <h2 className="text-[17px] font-bold leading-snug text-gray-900">{row.name}</h2>
          </div>
          {canScope && row.id && (
            <button onClick={onScope} className="text-[11.5px] font-semibold text-brand-600 hover:text-brand-700 mt-1">Scope / edit</button>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 mt-0.5"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {row.status === 'Blocked' && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3.5 py-3 mb-4 text-[12.5px] text-red-700 leading-relaxed">
              <b>Blocked at classification gate.</b> Classified <b>prohibited</b> under the EU AI Act — cannot proceed to build. No approval path exists for this class.
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3.5">
            <DrawerField label="Department">{dash(row.department)}</DrawerField>
            <DrawerField label="Capability">{row.capability_tag ? <Pill text={row.capability_tag} mono cls="bg-brand-50 text-brand-700" /> : dash(null)}</DrawerField>
            <DrawerField label="Risk"><RiskPill risk={row.risk} /></DrawerField>
            <DrawerField label="EU AI Act class"><Pill text={row.eu_ai_act_class} cls={EU_CLS[row.eu_ai_act_class || '']} /></DrawerField>
            <DrawerField label="NIST function"><Pill text={row.nist_function} mono cls="bg-purple-50 text-purple-700" /></DrawerField>
            <DrawerField label="ISO 42001">{dash(row.iso_control)}</DrawerField>
            <DrawerField label="Owner">{dash(row.owner)}</DrawerField>
            <DrawerField label="Tool / vendor">{dash(row.tool_vendor)}</DrawerField>
            <DrawerField label="Model" wide>{dash(row.model)}</DrawerField>
            <DrawerField label="Data flow · residency" wide>{dash(row.data_flow)}</DrawerField>
            <DrawerField label="Human review" wide>{dash(row.human_in_loop, '— unset')}</DrawerField>
            <DrawerField label="Est. cost / value" wide>{row.est_cost_band === '—' && row.est_value_band === '—' ? <span className="text-gray-300">—</span> : costValue}</DrawerField>
            {row.scope_notes && <DrawerField label="Scoping notes" wide>{row.scope_notes}</DrawerField>}
          </div>
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="font-sans text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-2.5">Approval trail</div>
            {trail.map((t, i) => (
              <div key={i} className="flex gap-2.5 mb-3">
                <span className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${t.tone === 'green' ? 'bg-emerald-600' : t.tone === 'red' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <div>
                  <div className="text-[12.5px] font-semibold text-gray-800">{t.who}</div>
                  <div className="text-[12px] text-gray-500">{t.what}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {showEvalCta && (
          <div className="shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/70">
            <button onClick={evaluate}
              className="w-full inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg px-3 py-2.5 text-[13px] font-semibold whitespace-nowrap">
              <Scale size={14} /> {row.evaluation_id ? `View evaluation of ${row.tool_vendor}` : `Evaluate ${row.tool_vendor}`}
            </button>
            <div className="text-[11px] text-gray-400 text-center mt-1.5">Closes the loop — sends this tool to the Vendor Evaluator.</div>
          </div>
        )}
      </div>
    </>
  );
};

type TrailStep = { tone: 'green' | 'amber' | 'red'; who: string; what: string };

function buildTrail(r: RegistryRow): TrailStep[] {
  if (r.status === 'Blocked') {
    return [{ tone: 'red', who: 'Classification gate', what: 'Prohibited — approval refused automatically' }];
  }
  if (r.status === 'Shadow AI') {
    return [
      { tone: 'amber', who: r.requester_email ? `Logged by ${r.requester_email}` : 'Self-report', what: `Logged as inventory${r.created_at ? ` · ${fmtDate(r.created_at)}` : ''} — awaiting classification` },
      { tone: 'amber', who: 'Champion', what: 'To triage: classify or evaluate the tool' },
    ];
  }
  return [
    { tone: 'green', who: r.requester_email || 'Requester', what: `Submitted via requests${r.created_at ? ` · ${fmtDate(r.created_at)}` : ''}` },
    { tone: 'green', who: 'Department head', what: 'Gate 1 — approved' },
    { tone: 'green', who: r.approved_by || 'Champion / owner', what: `Gate 2 — final approval recorded${r.approved_at ? ` · ${fmtDate(r.approved_at)}` : ''}` },
  ];
}

// ── modal shell + shadow capture + scope form ───────────────────────────────

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[70] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl p-5 w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block mb-3">
    <span className="block text-[11.5px] font-semibold text-gray-700 mb-1">{label}</span>
    {children}
  </label>
);

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200';

const ShadowModal: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [tool, setTool] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await aaiApi.createRequest({ title: title.trim(), tool_vendor: tool.trim() || undefined, source: 'shadow_ai', capability_tag: 'other' });
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Log shadow AI" onClose={onClose}>
      <p className="text-[12.5px] text-gray-500 mb-3">Capture AI already in use without formal approval. It enters the registry as an inventory item to govern — not a request to approve.</p>
      <Field label="What is it being used for?">
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Drafting client emails in ChatGPT" className={inputCls} />
      </Field>
      <Field label="Tool / vendor">
        <input value={tool} onChange={(e) => setTool(e.target.value)} placeholder="e.g. ChatGPT (personal account)" className={inputCls} />
      </Field>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">Cancel</button>
        <button disabled={busy || !title.trim()} onClick={save} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 text-white disabled:opacity-50 whitespace-nowrap">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add to registry</button>
      </div>
    </Modal>
  );
};

const ScopeModal: React.FC<{ row: RegistryRow; onClose: () => void; onSaved: () => void }> = ({ row, onClose, onSaved }) => {
  const [f, setF] = useState({
    owner_email: row.owner && row.owner !== '—' ? row.owner : '',
    target_phase: row.target_phase && row.target_phase !== '—' ? row.target_phase : '',
    capability_tag: row.capability_tag || '',
    eu_ai_act_class: row.eu_ai_act_class || '',
    nist_function: row.nist_function || '',
    iso_control: row.iso_control || '',
    tool_vendor: row.tool_vendor && row.tool_vendor !== '—' ? row.tool_vendor : '',
    model: row.model && row.model !== '—' ? row.model : '',
    human_in_loop: row.human_in_loop && row.human_in_loop !== 'Not set' ? row.human_in_loop : '',
    data_flow: row.data_flow && row.data_flow !== '—' ? row.data_flow : '',
    est_cost_band: row.est_cost_band && row.est_cost_band !== '—' ? row.est_cost_band : '',
    est_value_band: row.est_value_band && row.est_value_band !== '—' ? row.est_value_band : '',
    scope_notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!row.id) return;
    setBusy(true); setErr(null);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v)) as Record<string, string>;
      await aaiApi.scopeRequest(row.id, body);
      onSaved();
    } catch (e) {
      const x = e as { response?: { data?: { detail?: string } } };
      setErr(x.response?.data?.detail || 'Could not save the scoping.');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Scope · ${row.name}`} onClose={onClose}>
      <p className="text-[12px] text-gray-500 mb-3">Assign an owner, record the examiner classification, and fill the lens fields (ranges over fake precision). Only filled fields are updated.</p>
      <Field label="Owner (email)"><input value={f.owner_email} onChange={(e) => set('owner_email', e.target.value)} className={inputCls} /></Field>
      <Field label="Target phase"><input value={f.target_phase} onChange={(e) => set('target_phase', e.target.value)} placeholder="e.g. Application" className={inputCls} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Capability">
          <select value={f.capability_tag} onChange={(e) => set('capability_tag', e.target.value)} className={`${inputCls} capitalize`}>
            <option value="">—</option>{CAPABILITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="EU AI Act class">
          <select value={f.eu_ai_act_class} onChange={(e) => set('eu_ai_act_class', e.target.value)} className={`${inputCls} capitalize`}>
            <option value="">—</option>{EU_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="NIST function">
          <select value={f.nist_function} onChange={(e) => set('nist_function', e.target.value)} className={`${inputCls} capitalize`}>
            <option value="">—</option>{NIST_FUNCTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="ISO 42001 control"><input value={f.iso_control} onChange={(e) => set('iso_control', e.target.value)} placeholder="e.g. A.6.2.2" className={inputCls} /></Field>
        <Field label="Tool / vendor"><input value={f.tool_vendor} onChange={(e) => set('tool_vendor', e.target.value)} className={inputCls} /></Field>
        <Field label="Model"><input value={f.model} onChange={(e) => set('model', e.target.value)} placeholder="e.g. Claude Sonnet" className={inputCls} /></Field>
        <Field label="Human review"><input value={f.human_in_loop} onChange={(e) => set('human_in_loop', e.target.value)} placeholder="e.g. Reviewer signs off" className={inputCls} /></Field>
        <Field label="Est. cost / yr"><input value={f.est_cost_band} onChange={(e) => set('est_cost_band', e.target.value)} placeholder="e.g. $8–12k" className={inputCls} /></Field>
        <Field label="Est. value / yr"><input value={f.est_value_band} onChange={(e) => set('est_value_band', e.target.value)} placeholder="e.g. $60–90k" className={inputCls} /></Field>
      </div>
      <Field label="Data flow · sub-processors · residency"><input value={f.data_flow} onChange={(e) => set('data_flow', e.target.value)} placeholder="e.g. US · Anthropic · no training" className={inputCls} /></Field>
      <Field label="Scoping notes"><textarea value={f.scope_notes} onChange={(e) => set('scope_notes', e.target.value)} rows={2} className={inputCls} /></Field>
      {err && <p className="text-[12px] text-red-600 mb-2">{err}</p>}
      <div className="flex justify-end gap-2 mt-1">
        <button onClick={onClose} className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700">Cancel</button>
        <button disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 text-white disabled:opacity-50 whitespace-nowrap">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save scoping</button>
      </div>
    </Modal>
  );
};

export default RegistryPage;
